<template>
  <div :class="rootClass">
    <BravadoText
      v-if="label"
      tag="label"
      variant="body-semibold"
      class="bravado-text-input-v3__label"
      data-test="bravado-text-input-v3__label"
    >
      {{ label }}
      <span v-if="mandatory" class="bravado-text-input-v3__label-mandatory">*</span>
    </BravadoText>
    <div :class="containerClass">
      <div v-if="readonlyText && !multiline" ref="readOnlyText" class="bravado-text-input-v3__read-only-text">
        {{ readonlyText }}
      </div>
      <textarea
        v-if="multiline"
        ref="input"
        v-bind="inputProps"
        class="bravado-text-input-v3__field-textarea"
        data-test="input"
        v-on="inputListeners"
      />

      <input
        v-else
        ref="input"
        v-mask="dynamicMask"
        data-test="input"
        :type="type"
        v-bind="inputProps"
        v-on="inputListeners"
      />

      <div v-if="$slots.icon" class="bravado-text-input-v3__icon" @click="handleIconClick">
        <slot name="icon" />
      </div>

      <div v-if="$slots.append" class="bravado-text-input-v3__append" @click="handleIconClick">
        <slot name="append" />
      </div>
    </div>
    <BravadoText
      v-if="hint"
      variant="caption2"
      class="bravado-text-input-v3__hint"
      data-test="bravado-text-input-v3__hint"
    >
      {{ hint }}
    </BravadoText>
  </div>
</template>

<script lang="ts">
import type { PropOptions } from 'vue';
import Vue from 'vue';
// @ts-ignore
import { VueMaskDirective } from 'v-mask';
import { onFontsReady } from '@/components/utils/browser/core';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';

interface IData {
  dynamicMask: string;
  isFocused: boolean;
}

export default Vue.extend({
  name: 'BravadoTextInputV3',
  components: { BravadoText },
  directives: {
    mask: VueMaskDirective,
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
      required: false,
    } as PropOptions<string | number>,
    valueFormatter: {
      type: Function,
      default: null,
    } as PropOptions<(<T>(val: T) => T) | null>,
    valueParser: {
      type: Function,
      default: null,
    } as PropOptions<(<T>(val: T) => T) | null>,
    isValid: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
    label: {
      type: String,
      default: '',
    } as PropOptions<string>,
    hint: {
      type: String,
      default: '',
    } as PropOptions<string>,
    multiline: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    inputStyle: {
      type: Object,
      default: () => ({}),
    } as PropOptions<Record<string, string>>,
    maxlength: {
      type: Number,
      default: null,
    } as PropOptions<number>,
    mandatory: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    variant: {
      type: String,
      default: 'default',
      validator: (value: string) => ['default', 'borderless'].includes(value),
    } as PropOptions<string>,
    mask: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    maskFormatter: {
      type: Function,
      default: null,
    } as PropOptions<(<T>(value: string, initialMask: string, currentMask: string) => T) | null>,
    size: {
      type: String,
      default: 'md',
      validator: (value: string) => ['sm', 'md'].includes(value),
    } as PropOptions<'sm' | 'md'>,
    readonlyText: {
      type: String,
      default: '',
    } as PropOptions<string>,
    type: {
      type: String,
      default: 'text',
    } as PropOptions<'text' | 'number' | 'search' | 'email'>,
    /**
     * Slot/icon has interactivity on it, otherwise - focus on input on click
     */
    interactiveSlot: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
  },
  data(): IData {
    return {
      dynamicMask: this.mask,
      isFocused: false,
    };
  },
  computed: {
    inputProps(): Record<string, unknown> {
      const value = this.valueFormatter && this.value ? this.valueFormatter(this.value) : this.value;

      return {
        ref: 'input',
        autocomplete: 'off',
        class: 'bravado-text-input-v3__field',
        style: this.inputStyle,
        maxlength: this.maxlength,
        value,
        ...this.$attrs,
      };
    },
    inputListeners(): Record<string, Function> {
      return {
        ...this.$listeners,
        input: this.handleInput,
        focus: this.handleFocus,
        blur: this.handleBlur,
      };
    },
    rootClass(): Record<string, boolean> {
      return {
        'bravado-text-input-v3': true,
        'bravado-text-input-v3--icon': Boolean(this.$slots.icon),
        'bravado-text-input-v3--append': Boolean(this.$slots.append),
        'bravado-text-input-v3--invalid': !this.isValid,
        'bravado-text-input-v3--interactive-slot': this.interactiveSlot,
        [`bravado-text-input-v3--${this.variant}`]: true,
        [`bravado-text-input-v3--${this.size}`]: Boolean(this.size),
      };
    },
    containerClass(): Record<string, boolean> {
      return {
        'bravado-text-input-v3__container': true,
      };
    },
  },
  mounted(): void {
    this.focusOnMounted();

    onFontsReady(this.setInputPadding);
  },
  methods: {
    handleIconClick(): void {
      this.$emit('handleIconClick');
      if (this.$refs.input) (this.$refs.input as HTMLElement).focus();
    },
    blur(): void {
      (this.$refs.input as HTMLTextAreaElement).blur();
    },
    focus(): void {
      (this.$refs.input as HTMLTextAreaElement).focus();
    },
    focusOnMounted(): void {
      if (!this.autofocus) return;

      this.$nextTick(() => {
        const input = this.$el.querySelector('input');
        const textarea = this.$el.querySelector('textarea');

        if (input) input.focus();
        else if (textarea) textarea.focus();
      });
    },
    handleInput(event: Event): void {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      const value = this.valueParser ? this.valueParser(target.value) : target.value;

      if (this.maskFormatter) {
        this.dynamicMask = this.maskFormatter(target.value, this.mask, this.dynamicMask);
      }

      if (target.nodeName === 'TEXTAREA') {
        target.style.height = '76px';
        target.style.height = target.scrollHeight + 12 + 'px';
      }

      this.$emit('input', value);
    },
    handleFocus(event: Event): void {
      this.isFocused = true;
      this.$emit('focus', event);
    },
    handleBlur(event: Event): void {
      this.isFocused = false;
      this.$emit('blur', event);
    },
    setInputPadding() {
      if (!this.$refs.readOnlyText) return;

      const INPUT_LEFT_PADDING = 16;

      const { width } = (this.$refs.readOnlyText as HTMLElement).getBoundingClientRect();

      (this.$refs.input as HTMLElement).style.paddingLeft = `${width + INPUT_LEFT_PADDING}px`;
    },
  },
});
</script>

<style scoped lang="scss">
.bravado-text-input-v3 {
  $this: &;

  position: relative;

  &__container {
    position: relative;
  }

  &__read-only-text {
    color: var(--gray-50);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    left: 16px;
  }

  &__field {
    @include text-overflow;
    @include body;

    width: 100%;
    height: 48px;
    background: var(--theme-color-card);
    color: var(--theme-color-text);
    padding: 12px 16px;
    appearance: none;
    border: 1px solid var(--theme-color-border);
    border-radius: 4px;
    outline: 2px solid transparent;
    outline-offset: 0;
    transition: outline-color 0.2s, border-color 0.2s;

    &[disabled] {
      background: var(--theme-color-over-card);
      color: rgba(var(--theme-text-rgb), 0.3);
      cursor: not-allowed;
    }

    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus-visible {
      outline-color: var(--blue-60-a15);
      border-color: var(--blue-60);
    }

    #{$this}--borderless & {
      border-width: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }

  &__field-textarea {
    height: 88px;
    white-space: normal;
    overflow: auto;
    resize: none;
  }

  &__icon,
  &__append {
    cursor: pointer;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(var(--theme-text-rgb), 0.5);
    display: flex;
    align-items: center;
    pointer-events: none;

    #{$this}--interactive-slot & {
      pointer-events: all;
    }
  }

  &__icon {
    line-height: 0;
    left: 12px;
  }

  &__append {
    right: 16px;
  }

  &__label {
    display: flex;
    color: var(--theme-color-text);
    margin-bottom: 6px;
  }

  &__hint {
    text-align: left;
    margin-top: 4px;
    color: var(--theme-color-sub-text);
  }

  &--icon {
    #{$this}__field {
      padding-left: 44px;
    }
  }

  &--append {
    #{$this}__field {
      padding-right: 44px;
    }
  }

  &--invalid {
    #{$this}__container {
      border-color: var(--red-50);
    }

    #{$this}__hint {
      color: var(--red-50);
    }

    &#{$this}--borderless {
      #{$this}__field {
        &:not(:disabled)::placeholder {
          color: var(--red-50);
        }
      }
    }
  }

  &--borderless {
    #{$this}__container {
      background-color: transparent;
      border-color: transparent;
    }

    #{$this}__field {
      color: var(--blue-60);
      font-weight: 600;
      padding-left: 0;
      padding-right: 0;

      &[disabled] {
        color: var(--blue-60-a50);
        background-color: transparent;
      }

      &:not(:disabled)::placeholder {
        color: var(--blue-60);
      }

      &::placeholder {
        font-weight: 400;
        color: var(--gray-50);
      }
    }
  }

  &--sm {
    #{$this}__field {
      padding: 0 12px;
      height: 36px;
    }

    &#{$this}--icon {
      #{$this}__field {
        padding-left: 40px;
      }
    }
  }
}
</style>
